/**
 * ── TAG-BALANCE GATE ON EVERY SFMC BLOCK (2026-09-10) ─────────────────────────────
 *
 * LITMUS-OPEN-FOR-TOURS-CHI-2026-09-10: a 7:36 AM save of ECH_Featured2CTA and
 * ECH_Featured1CTA appended ONE extra `</td></tr></table>` to each block. The Hub
 * places a block as one row of the 600px wrapper, so card 1's stray close shut the
 * wrapper and every block after it rendered at full window width — photos at the far
 * left, help section and footer edge to edge, the last card at a different text size on
 * every iPhone. Forty-plus Litmus captures, one missing character class.
 *
 * `/sfmc-blocks` trusted the markup the same way the fill trusted image URLs before
 * echLiveImg (#208): the feed's presence is not its validity. This module is the gate.
 *
 * WHAT IT DOES
 *   · counts `<table` / `<tr` / `<td` opens against their closes, OUTSIDE Outlook
 *     conditional comments (`<!--[if …]> … <![endif]-->` — Outlook-only markup is
 *     allowed to be unbalanced from the browser's point of view by design) and outside
 *     ordinary comments;
 *   · when the ONLY defect is surplus closers at the very END of the block — the shape
 *     a hand-merge produces — it trims exactly those and reports the repair;
 *   · anything else (an unclosed table, a stray close mid-block) is left as written
 *     and reported, because a guess there could hide a real authoring error.
 *
 * WHAT IT DOES NOT DO: rewrite content, reorder tags, touch guardrails (the guardrail
 * comment is already stripped before this runs), or hide the problem — every
 * imbalance is a warning on the module and a console line, so the tell shows up in
 * dev before it shows up in Litmus.
 */

export type BalanceTag = "table" | "tr" | "td";
export const BALANCE_TAGS: BalanceTag[] = ["table", "tr", "td"];

export interface TagCount {
  open: number;
  close: number;
}

export interface BalanceReport {
  /** markup after any repair (identical to the input when nothing was trimmed) */
  html: string;
  /** opens vs closes per tag, measured on the RESULT */
  counts: Record<BalanceTag, TagCount>;
  /** true when every counted tag balances on the result */
  balanced: boolean;
  /** the closers removed from the tail, in order, when a tail repair was possible */
  trimmed: string[];
  /** human-readable findings — empty when the block was clean */
  warnings: string[];
}

/* Outlook conditionals first (they may contain plain comments), then plain comments. */
const CONDITIONAL_RE = /<!--\[if[\s\S]*?<!\[endif\]-->/gi;
const COMMENT_RE = /<!--(?!\[if)[\s\S]*?-->/g;

/**
 * Markup as a browser sees it: no conditional regions, no comments.
 * Strips to a fixed point so a nested or overlapping opener (`<!--<!-- x -->`) cannot
 * survive one pass — CodeQL "incomplete multi-character sanitization" on PR #227. The
 * result is only ever COUNTED, never rendered, but the loop costs nothing and the check
 * stays green.
 */
export function browserView(html: string): string {
  let cur = String(html || "");
  for (;;) {
    const next = cur.replace(CONDITIONAL_RE, "").replace(COMMENT_RE, "");
    if (next === cur) return cur;
    cur = next;
  }
}

export function countTags(html: string): Record<BalanceTag, TagCount> {
  const seg = browserView(html);
  const out = {} as Record<BalanceTag, TagCount>;
  for (const t of BALANCE_TAGS) {
    const open = (seg.match(new RegExp(`<${t}\\b`, "gi")) || []).length;
    const close = (seg.match(new RegExp(`</${t}\\s*>`, "gi")) || []).length;
    out[t] = { open, close };
  }
  return out;
}

const isBalanced = (c: Record<BalanceTag, TagCount>) => BALANCE_TAGS.every((t) => c[t].open === c[t].close);

/* one trailing closer, with any whitespace before it */
const TAIL_CLOSER_RE = /\s*<\/(table|tr|td)\s*>\s*$/i;
const TOKEN_RE = /<(\/?)(table|tr|td)\b[^>]*>/gi;

/**
 * Walk the browser's view with a nesting stack. Classifies every close of a counted tag:
 *   · `mismatch` — closes a tag that is not the innermost open one (a `</table>` while a
 *     `<td>` is open): a structural error somewhere INSIDE the block;
 *   · `surplus` — closes on an empty stack: nothing left to close, i.e. an extra closer.
 * A tail repair is allowed only when there are NO mismatches and every surplus closer is
 * in the trailing run — which is exactly what a hand-merge that appends
 * `</td></tr></table>` produces, and nothing else.
 */
function classify(view: string): { mismatches: number; surplus: number; surplusAllAtTail: boolean; unclosed: number } {
  const stack: string[] = [];
  let mismatches = 0;
  let surplus = 0;
  let lastNonSurplusEnd = 0; // end offset of the last open/legit close
  let firstSurplusStart = -1;
  let m: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(view))) {
    const tag = m[2].toLowerCase();
    if (m[1] !== "/") {
      stack.push(tag);
      lastNonSurplusEnd = m.index + m[0].length;
      continue;
    }
    if (!stack.length) {
      surplus += 1;
      if (firstSurplusStart < 0) firstSurplusStart = m.index;
      continue;
    }
    if (stack[stack.length - 1] !== tag) mismatches += 1;
    stack.pop();
    lastNonSurplusEnd = m.index + m[0].length;
  }
  /* surplus closers are "at the tail" when nothing but whitespace sits between the last
     legitimate token and the first surplus closer, and nothing but closers follow */
  const between = firstSurplusStart >= 0 ? view.slice(lastNonSurplusEnd, firstSurplusStart) : "";
  const tail = firstSurplusStart >= 0 ? view.slice(firstSurplusStart) : "";
  const surplusAllAtTail =
    surplus > 0 && lastNonSurplusEnd <= firstSurplusStart && /^\s*$/.test(between) && /^(\s*<\/(table|tr|td)\s*>)+\s*$/i.test(tail);
  return { mismatches, surplus, surplusAllAtTail, unclosed: stack.length };
}

/** Remove exactly `n` closers from the tail of the ORIGINAL markup (comments kept). */
function trimTail(html: string, n: number): { html: string; trimmed: string[] } {
  const trimmed: string[] = [];
  let cur = html;
  while (trimmed.length < n) {
    const m = cur.match(TAIL_CLOSER_RE);
    if (!m) break;
    cur = cur.slice(0, cur.length - m[0].length);
    trimmed.push(`</${m[1].toLowerCase()}>`);
  }
  return { html: cur, trimmed };
}

/** Gate one block's markup. Pure; safe to call on every fetch. */
export function balanceBlockMarkup(html: string, name = "block"): BalanceReport {
  const input = String(html || "");
  const before = countTags(input);
  if (isBalanced(before)) return { html: input, counts: before, balanced: true, trimmed: [], warnings: [] };

  const overClosed = BALANCE_TAGS.filter((t) => before[t].close > before[t].open);
  const underClosed = BALANCE_TAGS.filter((t) => before[t].open > before[t].close);
  const warnings: string[] = [];

  let result = input;
  let trimmed: string[] = [];
  const shape = classify(browserView(input));
  if (overClosed.length && !underClosed.length && shape.mismatches === 0 && shape.unclosed === 0 && shape.surplusAllAtTail) {
    ({ html: result, trimmed } = trimTail(input, shape.surplus));
  }
  const after = countTags(result);
  const balanced = isBalanced(after);

  const describe = (c: Record<BalanceTag, TagCount>) =>
    BALANCE_TAGS.filter((t) => c[t].open !== c[t].close)
      .map((t) => `${t} ${c[t].open}/${c[t].close}`)
      .join(", ");

  if (trimmed.length) {
    warnings.push(`${name}: trimmed ${trimmed.length} surplus trailing closer${trimmed.length === 1 ? "" : "s"} (${trimmed.join("")}) — was ${describe(before)}`);
  }
  if (!balanced) {
    warnings.push(`${name}: markup still unbalanced after gate (${describe(after)}) — left as written; fix the block in Content Builder`);
  }
  return { html: result, counts: after, balanced, trimmed, warnings };
}

/** Console surface, kept in one place so tests can silence it. */
export function reportBalance(rep: BalanceReport): void {
  for (const w of rep.warnings) {
    // eslint-disable-next-line no-console
    console.warn(`[sfmc-blocks] ${w}`);
  }
}
